// externalData.ts - Updated to implement ChartDataStore

import {
  dataPointPool,
  normalizeChartDataPoint,
  normalizeChartDataPoints,
  type ChartDataInputPoint,
  type ChartDataPoint,
} from "./data";
import type { Timeframe } from "./types";

export type ExternalDataOptions = {
  symbol?: string;
  pipSize?: number;
  initialData?: Array<ChartDataInputPoint | ChartDataPoint>;
};

export type ExternalDataFeed = {
  // Data management
  setData(points: Array<ChartDataInputPoint | ChartDataPoint>): void;
  append(point: ChartDataInputPoint | ChartDataPoint): void;
  appendMany(points: Array<ChartDataInputPoint | ChartDataPoint>): void;
  getData(): ChartDataPoint[];
  getLatestTick(): ChartDataPoint | null;
  clearData(): void;
  clear(): void;  // Alias for clearData
  
  // Receive raw data (frontend pushes data here)
  receive(raw: unknown): void;
  
  // Listeners
  addListener(callback: (data: ChartDataPoint[]) => void): void;
  removeListener(callback: (data: ChartDataPoint[]) => void): void;
  subscribe(callback: () => void): () => void;  // For ChartDataStore compatibility
  
  // Market related
  getSymbols(): { symbol: string; display_name: string }[];
  getSelectedSymbol(): string;
  selectSymbol(symbol: string): void;
  addMarketListener(callback: () => void): void;
  removeMarketListener(callback: () => void): void;
  forceUpdate(): void;
  
  // No-op methods for compatibility with LiveDataFeed interface
  start(): void;
  stop(): void;
  configure(config: Partial<ExternalDataOptions>): void;
  updateForTimeframe(timeframe: Timeframe): void;
  ensureDataForTimeframe(timeframe: Timeframe): Promise<void>;
};

class ExternalDataManager implements ExternalDataFeed {
  private data: ChartDataPoint[] = [];
  private config: ExternalDataOptions;
  private listeners: ((data: ChartDataPoint[]) => void)[] = [];
  private marketListeners: (() => void)[] = [];
  private dataChangeListeners: (() => void)[] = [];
  private symbol: string = "";
  private symbols: { symbol: string; display_name: string }[] = [];

  constructor(options: ExternalDataOptions = {}) {
    this.config = {
      symbol: options.symbol || "VIX_10",
      pipSize: options.pipSize || 3,
      initialData: options.initialData || [],
    };
    this.symbol = this.config.symbol!;
    
    // Initialize with default symbol
    this.symbols = [{ symbol: this.symbol, display_name: this.symbol }];
    
    // Set initial data if provided
    if (this.config.initialData && this.config.initialData.length > 0) {
      this.setData(this.config.initialData);
    }
  }

  configure(config: Partial<ExternalDataOptions>): void {
    this.config = { ...this.config, ...config };
    if (config.symbol) {
      this.symbol = config.symbol;
      // Update symbols list
      const existing = this.symbols.find(s => s.symbol === this.symbol);
      if (!existing) {
        this.symbols.push({ symbol: this.symbol, display_name: this.symbol });
      }
    }
  }

  // Data management
  setData(points: Array<ChartDataInputPoint | ChartDataPoint>): void {
    // Release old data
    for (const point of this.data) {
      dataPointPool.release(point);
    }
    
    this.data = normalizeChartDataPoints(points, this.symbol, this.config.pipSize || 3);
    this.notifyListeners();
    this.notifyDataChange();
  }

  appendMany(points: Array<ChartDataInputPoint | ChartDataPoint>): void {
    const normalized = normalizeChartDataPoints(points, this.symbol, this.config.pipSize || 3);
    for (const point of normalized) {
      this.data.push(dataPointPool.acquire(point.epoch, point.quote, point.symbol, point.precision ?? point.pip_size ?? 0));
    }
    this.data.sort((a, b) => a.epoch - b.epoch);
    this.trimData();
    this.notifyListeners();
    this.notifyDataChange();
  }

  getData(): ChartDataPoint[] {
    return this.data;
  }

  getLatestTick(): ChartDataPoint | null {
    return this.data.length > 0 ? this.data[this.data.length - 1] : null;
  }

  clearData(): void {
    for (const point of this.data) {
      dataPointPool.release(point);
    }
    this.data = [];
    this.notifyListeners();
    this.notifyDataChange();
  }

  clear(): void {
    this.clearData();
  }

  private trimData(): void {
    const maxDataPoints = 100000;
    if (this.data.length > maxDataPoints) {
      const removed = this.data.splice(0, this.data.length - maxDataPoints);
      for (const point of removed) {
        dataPointPool.release(point);
      }
    }
  }

  // Receive raw data from frontend
  receive(raw: unknown): void {
    let dataProcessed = false;
    
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        dataProcessed = this.processReceivedData(parsed);
      } catch {
        dataProcessed = this.processReceivedData(raw);
      }
    } else if (Array.isArray(raw)) {
      this.appendMany(raw as ChartDataInputPoint[]);
      dataProcessed = true;
    } else if (typeof raw === "object" && raw !== null) {
      dataProcessed = this.processReceivedData(raw);
    }
    
    //Ensure listeners are notified if data was processed
    if (dataProcessed) {
      // Force immediate notification to update the market button
      this.notifyListeners();
      this.notifyDataChange();
    }
  }

  // Also update append to notify immediately:
  append(point: ChartDataInputPoint | ChartDataPoint): void {
    const normalized = normalizeChartDataPoint(point, this.symbol, this.config.pipSize || 3);
    this.data.push(dataPointPool.acquire(normalized.epoch, normalized.quote, normalized.symbol, normalized.precision ?? normalized.pip_size ?? 0));
    this.data.sort((a, b) => a.epoch - b.epoch);
    this.trimData();
    // Notify immediately
    this.notifyListeners();
    this.notifyDataChange();
  }

  // In externalData.ts, add this method to ExternalDataManager:

  setSymbols(symbols: { symbol: string; display_name: string }[]): void {
    this.symbols = symbols.map(s => ({
      symbol: s.symbol || "",
      display_name: s.display_name || s.symbol || ""
    }));
    // Also update current symbol if it exists in the new list
    const currentExists = this.symbols.some(s => s.symbol === this.symbol);
    if (!currentExists && this.symbols.length > 0) {
      this.symbol = this.symbols[0].symbol;
    }
    this.notifyMarketListeners();
    this.forceUpdate();
  }

  private toNumber(value: unknown): number | undefined {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return undefined;
  }

  private processReceivedData(data: unknown): boolean {
    // Check if it's a single tick
    const obj = data as Record<string, unknown>;
    
    // Check for tick format: { type: "tick", price: 123, epoch_ms: 123 }
    if (obj.type === "tick" && obj.price !== undefined) {
      const price = this.toNumber(obj.price);
      const epochMs = this.toNumber(obj.epoch_ms) ?? this.toNumber(obj.epoch) ?? Date.now();
      const symbol = typeof obj.symbol === "string" ? obj.symbol : this.symbol;
      const pipSize = this.toNumber(obj.precision) ?? this.toNumber(obj.pip_size) ?? (this.config.pipSize || 3);
      
      if (price !== undefined) {
        this.append({
          price,
          epoch_ms: epochMs,
          symbol,
          precision: pipSize,
          pip_size: pipSize,
        });
        return true;
      }
      return false;
    }
    
    // Check for ticks array in data
    if (obj.ticks && Array.isArray(obj.ticks)) {
      const ticks: ChartDataInputPoint[] = [];
      for (const tick of obj.ticks) {
        const price = this.toNumber(tick.price) ?? this.toNumber(tick.quote);
        const epochMs = this.toNumber(tick.epoch_ms) ?? this.toNumber(tick.epoch);
        const symbol = typeof tick.symbol === "string" ? tick.symbol : this.symbol;
        const pipSize = this.toNumber(tick.precision) ?? this.toNumber(tick.pip_size) ?? (this.config.pipSize || 3);
        
        if (price !== undefined && epochMs !== undefined) {
          ticks.push({
            price,
            epoch_ms: epochMs,
            symbol,
            precision: pipSize,
            pip_size: pipSize,
          });
        }
      }
      
      if (ticks.length > 0) {
        this.appendMany(ticks);
        return true;
      }
      return false;
    }
    
    // Check for candles
    if (obj.candles && Array.isArray(obj.candles)) {
      const candles: ChartDataInputPoint[] = [];
      for (const candle of obj.candles) {
        const price = this.toNumber(candle.close) ?? this.toNumber(candle.price);
        const epochMs = this.toNumber(candle.epoch) ?? this.toNumber(candle.time);
        const symbol = typeof candle.symbol === "string" ? candle.symbol : this.symbol;
        const pipSize = this.toNumber(candle.precision) ?? this.toNumber(candle.pip_size) ?? (this.config.pipSize || 3);
        
        if (price !== undefined && epochMs !== undefined) {
          candles.push({
            price,
            epoch_ms: epochMs,
            symbol,
            precision: pipSize,
            pip_size: pipSize,
          });
        }
      }
      
      if (candles.length > 0) {
        this.appendMany(candles);
        return true;
      }
      return false;
    }
    
    // Check for direct array
    if (Array.isArray(data)) {
      this.appendMany(data as ChartDataInputPoint[]);
      return true;
    }
    
    // Fallback: try to extract price and epoch
    const price = this.toNumber(obj.price) ?? this.toNumber(obj.quote);
    const epochMs = this.toNumber(obj.epoch_ms) ?? this.toNumber(obj.epoch) ?? this.toNumber(obj.time);
    const symbol = typeof obj.symbol === "string" ? obj.symbol : this.symbol;
    const pipSize = this.toNumber(obj.precision) ?? this.toNumber(obj.pip_size) ?? (this.config.pipSize || 3);
    
    if (price !== undefined && epochMs !== undefined) {
      this.append({
        price,
        epoch_ms: epochMs,
        symbol,
        precision: pipSize,
        pip_size: pipSize,
      });
      return true;
    }
    
    return false;
  }

  forceUpdate(): void {
    this.notifyListeners();
    this.notifyDataChange();
  }

  // Listeners
  addListener(callback: (data: ChartDataPoint[]) => void): void {
    this.listeners.push(callback);
  }

  removeListener(callback: (data: ChartDataPoint[]) => void): void {
    const index = this.listeners.indexOf(callback);
    if (index > -1) this.listeners.splice(index, 1);
  }

  subscribe(callback: () => void): () => void {
    this.dataChangeListeners.push(callback);
    // Return unsubscribe function
    return () => {
      const index = this.dataChangeListeners.indexOf(callback);
      if (index > -1) this.dataChangeListeners.splice(index, 1);
    };
  }

  private notifyListeners(): void {
    for (const callback of this.listeners) {
      callback(this.data);
    }
  }

  private notifyDataChange(): void {
    for (const callback of this.dataChangeListeners) {
      callback();
    }
  }

  // Market related
  getSymbols(): { symbol: string; display_name: string }[] {
    return [...this.symbols];
  }

  getSelectedSymbol(): string {
    return this.symbol;
  }

  selectSymbol(symbol: string): void {
    if (symbol && symbol !== this.symbol) {
      this.symbol = symbol;
      // Add to symbols list if not exists
      const existing = this.symbols.find(s => s.symbol === symbol);
      if (!existing) {
        this.symbols.push({ symbol, display_name: symbol });
      }
      this.clearData();
      this.notifyMarketListeners();
    }
  }

  addMarketListener(callback: () => void): void {
    this.marketListeners.push(callback);
  }

  removeMarketListener(callback: () => void): void {
    const index = this.marketListeners.indexOf(callback);
    if (index > -1) this.marketListeners.splice(index, 1);
  }

  private notifyMarketListeners(): void {
    for (const callback of this.marketListeners) {
      callback();
    }
  }

  // No-op methods for compatibility with LiveDataFeed
  start(): void {
    // No-op - no connection to start
  }

  stop(): void {
    // No-op - no connection to stop
  }

  updateForTimeframe(_timeframe: Timeframe): void {
    // No-op - external data doesn't manage timeframe
  }

  async ensureDataForTimeframe(_timeframe: Timeframe): Promise<void> {
    // No-op - external data doesn't fetch data
  }
}

export function createExternalDataFeed(options?: ExternalDataOptions): ExternalDataFeed {
  return new ExternalDataManager(options);
}

export function createExternalDataAdapter(options?: ExternalDataOptions): ExternalDataFeed {
  return new ExternalDataManager(options);
}
